import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import { Tree, Input, Button, Typography, Empty, Dropdown, Menu, Tooltip, } from "antd";
import { EllipsisOutlined, CaretRightOutlined, CaretDownOutlined, EditOutlined, DeleteOutlined, } from "@ant-design/icons";
const { Search } = Input;
const { Text } = Typography;
// 修改全局样式，添加调整间距的CSS
// 修改全局样式，调整箭头方向
const globalStyles = `
  .comps-tree .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-treenode {
    width: 100% !important;
    height: 32px !important;
    line-height: 32px !important;
  }

  /* 隐藏 antd 自带的拖拽小图标，只保留业务图标 */
  .comps-tree .ant-tree-draggable-icon {
    display: none !important;
  }
  
  .comps-tree .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-node-content-wrapper {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 4px !important;
    height: 32px !important;
    line-height: 32px !important;
  }
  
  /* 确保自定义图标可见并留出间距 */
  .comps-tree .ant-tree-icon__customize {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-right: 6px !important;
  }
  
  .comps-tree .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-title {
    width: 100% !important;
    min-width: 0 !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
  }
  
  /* 文字区域：占据剩余空间 */
  .comps-tree .ant-tree-title > div:first-child {
    min-width: 0 !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
  }
  
  /* 确保文本内容正确截断 */
  .comps-tree .ant-tree-title > div:first-child > span,
  .comps-tree .ant-tree-title > div:first-child > .ant-tooltip,
  .comps-tree .ant-tree-title > div:first-child > .ant-tooltip > span,
  .comps-tree .ant-tree-title > div:first-child > .ant-tooltip-wrapper,
  .comps-tree .ant-tree-title > div:first-child > .ant-tooltip-wrapper > span {
    display: block !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* 确保 Tooltip 包装器不会影响布局 */
  .comps-tree .ant-tree-title > div:first-child > .ant-tooltip,
  .comps-tree .ant-tree-title > div:first-child > .ant-tooltip-wrapper {
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    display: block !important;
  }
  
  /* 图标区域：独立区域，不收缩 */
  .comps-tree .ant-tree-title > div:last-child {
    flex-shrink: 0 !important;
    overflow: visible !important;
  }

  /* 方法徽标样式（用于接口节点前缀） */
  .api-method-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 18px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 700;
    border-radius: 4px;
    margin-right: 6px;
    border: 1px solid transparent;
  }
  
  .comps-tree .ant-tree-switcher .anticon {
    color: #bfbfbf !important;
  }
  
  .comps-tree .ant-tree-switcher {
    padding: 0 2px !important;
    width: 18px !important;
  }
  
  .comps-tree .tree-node-add-button {
    color: #bfbfbf !important;
  }
  
  .comps-tree .tree-node-add-button:hover {
    color: #1890ff !important;
  }
  
  .compact-tree .ant-tree-treenode {
    padding: 0 !important;
  }
  
  /* 紧凑模式样式 - 24px 行高 */
  .comps-tree.compact .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-treenode {
    height: 24px !important;
    line-height: 24px !important;
  }
  
  .comps-tree.compact .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-node-content-wrapper {
    height: 24px !important;
    line-height: 24px !important;
  }
  
  .comps-tree .ant-tree {
    width: 100% !important;
  }
  
  .comps-tree .ant-tree .ant-tree-list {
    width: 100% !important;
  }
  
  .comps-tree .ant-tree .ant-tree-list .ant-tree-list-holder {
    width: 100% !important;
  }
`;
/**
 * 通用树组件（内核）
 * 只包含树核心功能，不包含容器和标题栏
 */
const CompsTree = ({ treeData = [], loading = false, onSelect, onMenuClick, onDoubleClick, checkable = false, checkedKeys, onCheck, showSearch = true, showLine = true, showIcon = false, defaultExpandAll = true, defaultExpandedKeys = [], defaultSelectedKeys = [], menuItems = [], switcherIcon, selectedKeys, expandedKeys, onExpand, showActions = true, onEdit, onDelete, customMenuButton, className, style, compact = false, height, treeScrollHeight, searchPlaceholder = "搜索...", showTitleTooltip = true, blockNode = true, draggable = false, onTreeDataChange, }) => {
    // 状态定义
    const [searchValue, setSearchValue] = useState("");
    const [internalExpandedKeys, setInternalExpandedKeys] = useState(defaultExpandedKeys);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const [filteredTreeData, setFilteredTreeData] = useState(treeData);
    const containerRef = useRef(null);
    const treeContainerRef = useRef(null);
    /** 避免 defaultExpandAll 在父组件每次传入新 treeData 引用时反复执行，导致用户无法收拢节点 */
    const defaultExpandAllAppliedSigRef = useRef("");
    const [containerWidth, setContainerWidth] = useState(0); // 容器宽度
    const [treeContainerHeight, setTreeContainerHeight] = useState(0); // 树区域高度，传给 Tree 的 height 以启用内部滚动
    // 使用外部expandedKeys或内部状态
    const currentExpandedKeys = expandedKeys || internalExpandedKeys;
    // 监听树容器高度变化，用于传给 rc-tree height 以启用内部滚动
    useEffect(() => {
        const el = treeContainerRef.current;
        if (!el)
            return;
        const updateHeight = () => {
            const h = el.clientHeight || el.offsetHeight;
            setTreeContainerHeight(Math.max(0, h));
        };
        updateHeight();
        const ro = new ResizeObserver(updateHeight);
        ro.observe(el);
        return () => ro.disconnect();
    }, [filteredTreeData.length, height, showSearch]); // 父级 flex 高度就绪或搜索栏显隐后需重测
    // 监听容器宽度变化
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setContainerWidth(width);
            }
        };
        // 初始设置
        updateWidth();
        // 监听窗口大小变化
        const resizeObserver = new ResizeObserver(() => {
            updateWidth();
        });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    // 过滤树数据
    const filterTree = React.useCallback((value) => {
        const expandKeys = [];
        // 安全获取用于搜索的文本（title 可能为 ReactNode）
        const getNodeSearchText = (node) => {
            const parts = [];
            if (typeof node?.searchText === "string")
                parts.push(node.searchText);
            if (typeof node?.title === "string")
                parts.push(node.title);
            if (typeof node?.serviceName === "string")
                parts.push(node.serviceName);
            const p = node?.permission || node?.permissionData;
            if (p) {
                if (typeof p.code === "string")
                    parts.push(p.code);
                if (typeof p.page === "string")
                    parts.push(p.page);
                if (typeof p.title === "string")
                    parts.push(p.title);
                if (typeof p.name === "string")
                    parts.push(p.name);
            }
            return parts.join(" ").toLowerCase();
        };
        // 递归查找匹配节点及其父节点
        const filterData = (data) => {
            return data
                .map((node) => {
                const matchNode = getNodeSearchText(node).includes(value.toLowerCase());
                // 处理子节点
                const children = node.children ? filterData(node.children) : [];
                const matchChildren = children.length > 0;
                // 如果自身匹配或有匹配的子节点，则保留节点
                if (matchNode || matchChildren) {
                    expandKeys.push(node.key);
                    return {
                        ...node,
                        children: children.length > 0 ? children : undefined,
                    };
                }
                return null;
            })
                .filter((node) => node !== null);
        };
        const filtered = filterData(treeData);
        setFilteredTreeData(filtered);
        if (!expandedKeys) {
            setInternalExpandedKeys(expandKeys);
        }
        setAutoExpandParent(true);
    }, [treeData, expandedKeys]);
    // 当原始树数据变化时，更新过滤后的树数据
    useEffect(() => {
        if (searchValue) {
            filterTree(searchValue);
        }
        else {
            setFilteredTreeData(treeData);
        }
    }, [treeData, searchValue, filterTree]);
    // 当外部expandedKeys变化时，同步到内部状态
    useEffect(() => {
        if (expandedKeys) {
            setInternalExpandedKeys(expandedKeys);
        }
    }, [expandedKeys]);
    // defaultExpandAll：仅在树结构（key 集合）变化时展开全部，不因 treeData 引用抖动反复重置 expandedKeys
    useEffect(() => {
        if (treeData.length === 0) {
            defaultExpandAllAppliedSigRef.current = "";
        }
        if (!defaultExpandAll || treeData.length === 0 || expandedKeys) {
            return;
        }
        const getAllKeys = (nodes) => {
            let keys = [];
            nodes.forEach((node) => {
                keys.push(node.key);
                if (node.children && node.children.length > 0) {
                    keys = [...keys, ...getAllKeys(node.children)];
                }
            });
            return keys;
        };
        const allKeys = getAllKeys(treeData);
        const sig = allKeys.map(String).sort().join("\u0001");
        if (sig === defaultExpandAllAppliedSigRef.current) {
            return;
        }
        defaultExpandAllAppliedSigRef.current = sig;
        setInternalExpandedKeys(allKeys);
        setAutoExpandParent(true);
    }, [treeData, defaultExpandAll, expandedKeys]);
    // 处理搜索
    const handleSearch = (value) => {
        setSearchValue(value);
        if (value) {
            filterTree(value);
        }
        else {
            setFilteredTreeData(treeData);
            if (!expandedKeys) {
                setInternalExpandedKeys(defaultExpandedKeys);
            }
        }
    };
    // 处理展开/折叠：未受控（未传 expandedKeys）时必须同步内部状态，否则仅传 onExpand 时节点无法展开
    const handleExpand = (newExpandedKeys, info) => {
        onExpand?.(newExpandedKeys, info);
        if (!expandedKeys) {
            setInternalExpandedKeys(newExpandedKeys);
        }
        setAutoExpandParent(false);
    };
    // 渲染树节点标题，包括操作按钮
    const renderNodeTitle = (node, level = 0) => {
        // 使用节点自己的菜单项，如果没有则使用全局菜单项
        const nodeMenuItems = node.menuItems || menuItems || [];
        // 如果是接口节点，构建方法徽标
        const method = (node.apiData?.method || node.apiData?.httpMethod || "")
            .toString()
            .toUpperCase();
        const paletteMap = {
            GET: { bg: "#f6ffed", fg: "#52c41a", border: "#52c41a20" },
            POST: { bg: "#e6f4ff", fg: "#1677ff", border: "#1677ff20" },
            PUT: { bg: "#fff7e6", fg: "#fa8c16", border: "#fa8c1620" },
            PATCH: { bg: "#f9f0ff", fg: "#722ed1", border: "#722ed120" },
            DELETE: { bg: "#fff1f0", fg: "#ff4d4f", border: "#ff4d4f20" },
        };
        const palette = paletteMap[method] || {
            bg: "#f0f0f0",
            fg: "#999",
            border: "#99999920",
        };
        const menu = (_jsx(Menu, { onClick: ({ key }) => {
                onMenuClick && onMenuClick(node, key);
            }, children: nodeMenuItems.map((item) => (_jsx(Menu.Item, { children: item.label }, item.key))) }));
        // 获取节点标题文本（用于 Tooltip）；自定义 title 为 ReactNode 时可设 searchText
        const titleText = typeof node?.searchText === "string"
            ? node.searchText
            : typeof node.title === "string"
                ? node.title
                : "";
        // 计算右侧图标区域需要的宽度（预留空间）
        let iconAreaWidth = 8; // marginLeft
        if (showActions && onEdit)
            iconAreaWidth += 24; // 编辑按钮
        if (showActions && onDelete)
            iconAreaWidth += 24; // 删除按钮
        if (customMenuButton) {
            const customWidth = typeof node?.customMenuButtonWidth === "number"
                ? node.customMenuButtonWidth
                : 24;
            iconAreaWidth += customWidth; // 自定义按钮（可配置宽度）
        }
        else if (!customMenuButton &&
            onMenuClick &&
            nodeMenuItems &&
            nodeMenuItems.length > 0) {
            iconAreaWidth += 24; // 更多操作按钮
        }
        // 如果有多个按钮，需要加上 gap
        const buttonCount = [
            showActions && onEdit,
            showActions && onDelete,
            customMenuButton ||
                (!customMenuButton &&
                    onMenuClick &&
                    nodeMenuItems &&
                    nodeMenuItems.length > 0),
        ].filter(Boolean).length;
        if (buttonCount > 1) {
            iconAreaWidth += (buttonCount - 1) * 8; // gap 间距
        }
        const isCustomTitle = typeof node.title !== "string";
        const customTitleInner = (_jsx("div", { style: {
                width: "100%",
                minWidth: 0,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.45,
            }, children: node.title }));
        const stringTitleInner = (_jsx("span", { style: {
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                minWidth: 0,
            }, children: node.title }));
        const wrapTitle = (inner) => showTitleTooltip ? (_jsx(Tooltip, { title: isCustomTitle ? titleText || undefined : titleText, placement: "right", mouseEnterDelay: 0.5, overlayStyle: { maxWidth: "400px" }, children: inner })) : (inner);
        return (_jsxs("div", { style: {
                display: "grid",
                gridTemplateColumns: `1fr ${iconAreaWidth}px`,
                alignItems: isCustomTitle ? "flex-start" : "center",
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
                gap: "8px",
            }, children: [_jsx("div", { style: {
                        minWidth: 0,
                        overflow: "hidden",
                    }, children: isCustomTitle ? wrapTitle(customTitleInner) : wrapTitle(stringTitleInner) }), _jsxs("div", { style: {
                        display: "flex",
                        alignItems: isCustomTitle ? "flex-start" : "center",
                        justifyContent: "flex-end",
                        gap: "8px",
                        minWidth: `${iconAreaWidth}px`,
                        width: `${iconAreaWidth}px`,
                        flexShrink: 0,
                        paddingTop: isCustomTitle ? 2 : 0,
                    }, children: [showActions && onEdit && (_jsx(Button, { type: "text", icon: _jsx(EditOutlined, {}), size: "small", onClick: (e) => {
                                e.stopPropagation();
                                onEdit(node);
                            }, style: {
                                padding: "0 4px",
                                color: "#bfbfbf",
                                fontSize: "12px",
                            }, title: "\u7F16\u8F91" })), showActions && onDelete && (_jsx(Button, { type: "text", icon: _jsx(DeleteOutlined, {}), size: "small", onClick: (e) => {
                                e.stopPropagation();
                                onDelete(node);
                            }, style: {
                                padding: "0 4px",
                                color: "#bfbfbf",
                                fontSize: "12px",
                            }, title: "\u5220\u9664" })), customMenuButton && customMenuButton(node), !customMenuButton &&
                            onMenuClick &&
                            nodeMenuItems &&
                            nodeMenuItems.length > 0 && (_jsx(Dropdown, { overlay: menu, trigger: ["click"], children: _jsx(Button, { type: "text", icon: _jsx(EllipsisOutlined, {}), size: "small", onClick: (e) => e.stopPropagation(), style: {
                                    padding: "0 4px",
                                    color: "#bfbfbf",
                                    fontSize: "12px",
                                }, className: "tree-node-add-button", title: "\u66F4\u591A\u64CD\u4F5C" }) }))] })] }));
    };
    // 添加自定义标题到树数据
    const addCustomTitleToData = (data, level = 0) => {
        return data.map((node) => {
            const newNode = {
                ...node,
                title: renderNodeTitle(node, level),
            };
            if (node.children) {
                newNode.children = addCustomTitleToData(node.children, level + 1);
            }
            return newNode;
        });
    };
    // 带自定义标题的树数据
    const treeDataWithCustomTitle = addCustomTitleToData(filteredTreeData);
    // 处理拖拽排序（仅移动节点引用，不深拷贝，避免丢失 title/service 导致白屏）
    const handleDrop = (info) => {
        if (!draggable || !filteredTreeData?.length) {
            return;
        }
        const dropKey = info.node.key;
        const dragKey = info.dragNode.key;
        const dropPos = info.node.pos.split("-");
        const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
        const findParentAndIndex = (arr, key) => {
            for (let i = 0; i < arr.length; i += 1) {
                if (arr[i].key === key)
                    return { parentArr: arr, index: i };
                const ch = arr[i].children;
                if (ch?.length) {
                    const found = findParentAndIndex(ch, key);
                    if (found)
                        return found;
                }
            }
            return null;
        };
        // 浅拷贝结构、保留节点引用（title/service 等不丢失）
        const newTree = (filteredTreeData || []).map((g) => ({
            ...g,
            children: g.children ? [...g.children] : [],
        }));
        const dragLoc = findParentAndIndex(newTree, dragKey);
        if (!dragLoc)
            return;
        const dragObj = dragLoc.parentArr[dragLoc.index];
        dragLoc.parentArr.splice(dragLoc.index, 1);
        const dropLoc = findParentAndIndex(newTree, dropKey);
        if (!dropLoc)
            return;
        if (!info.dropToGap) {
            const dropNode = dropLoc.parentArr[dropLoc.index];
            const children = dropNode.children ? [...dropNode.children] : [];
            children.push(dragObj);
            dropNode.children = children;
        }
        else if ((info.node.children && info.node.children.length > 0 && info.node.expanded) &&
            dropPosition === 1) {
            const dropNode = dropLoc.parentArr[dropLoc.index];
            const children = dropNode.children ? [...dropNode.children] : [];
            children.unshift(dragObj);
            dropNode.children = children;
        }
        else {
            // 同层级插入：若在同一父节点且已先移除拖拽项，需修正插入下标
            let insertIdx;
            if (dragLoc.parentArr === dropLoc.parentArr && dropLoc.index >= dragLoc.index) {
                const newDropIdx = dropLoc.index - 1;
                insertIdx = dropPosition === -1 ? newDropIdx : newDropIdx + 1;
            }
            else {
                insertIdx = dropPosition === -1 ? dropLoc.index : dropLoc.index + 1;
            }
            dropLoc.parentArr.splice(insertIdx, 0, dragObj);
        }
        setFilteredTreeData(newTree);
        onTreeDataChange?.(newTree);
    };
    // 增强 switcherIconFunc 函数
    const switcherIconFunc = (props) => {
        const expanded = !!props.expanded;
        // 添加内联样式，确保不会被其他样式覆盖
        const iconStyle = {
            color: "#bfbfbf",
            transform: "none !important",
            transition: "none !important",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
        };
        if (expanded) {
            // 向下箭头 - 使用内置图标
            return _jsx(CaretDownOutlined, { style: iconStyle });
        }
        else {
            // 向右箭头 - 使用内置图标
            return _jsx(CaretRightOutlined, { style: iconStyle });
        }
    };
    /**
     * 高度与内部层级（供充满上级空间时参考）：
     * - 根容器：flex 列，height 为 props.height 或 "100%"，overflow: hidden；height="100%" 时加 flex:1 minHeight:0 以便在 flex 父节点下获得明确高度，使内部 .tree-container 可滚动
     * - 搜索区（若有）：flexShrink: 0，不参与高度分配
     * - 树区域 .tree-container：flex: 1, minHeight: 0, overflow: auto，负责内部滚动
     */
    const isFullHeight = height === "100%";
    const containerStyle = {
        display: "flex",
        flexDirection: "column",
        height: height
            ? typeof height === "number"
                ? `${height}px`
                : height
            : "100%",
        ...(isFullHeight && { flex: 1, minHeight: 0 }),
        overflow: "hidden",
        ...style,
    };
    const treeContainerStyle = {
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "auto",
        marginTop: "0",
        padding: "0",
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: globalStyles }), _jsxs("div", { ref: containerRef, className: `comps-tree ${compact ? "compact" : ""} ${className || ""}`, style: containerStyle, children: [showSearch && (_jsx("div", { className: "tree-search-container", style: {
                            flexShrink: 0,
                            marginBottom: "16px",
                        }, children: _jsx(Search, { placeholder: searchPlaceholder, allowClear: true, onChange: (e) => handleSearch(e.target.value) }) })), filteredTreeData.length > 0 ? (_jsx("div", { ref: treeContainerRef, className: "tree-container", style: treeContainerStyle, children: _jsx(Tree, { className: "full-width-tree compact-tree", blockNode: blockNode, checkable: checkable, showLine: false, showIcon: showIcon, draggable: draggable, height: treeScrollHeight ??
                                (treeContainerHeight > 0 ? treeContainerHeight : undefined), onExpand: handleExpand, expandedKeys: currentExpandedKeys, autoExpandParent: autoExpandParent, onSelect: onSelect, onDoubleClick: onDoubleClick, onDrop: handleDrop, onCheck: (keys, info) => {
                                if (onCheck) {
                                    const normalized = Array.isArray(keys)
                                        ? keys
                                        : keys.checked;
                                    onCheck(normalized, info);
                                }
                            }, checkedKeys: checkedKeys, defaultSelectedKeys: defaultSelectedKeys, selectedKeys: selectedKeys, defaultExpandAll: defaultExpandAll, treeData: treeDataWithCustomTitle, switcherIcon: switcherIconFunc, style: {
                                width: "100%",
                                height: height === "100%" ? "100%" : undefined,
                                fontSize: "14px",
                            } }) })) : (_jsx("div", { style: {
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }, children: _jsx(Empty, { description: "\u6682\u65E0\u6570\u636E" }) }))] })] }));
};
export default CompsTree;
